import { useCallback, useEffect, useState } from "react";
import { History, Eye, CheckCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { OPS } from "@/constants/testIds";
import { apiErrorText } from "@/lib/errorText";
import { fmtRiyadhDateTime } from "@/lib/format";

const C = OPS.checkin;
const RECOMPUTE_URL = "/branches/checkin/recompute-close";

// The counts shown after a preview: [counts key, i18n key].
const COUNTS = [
    ["scanned", "sys_rc_scanned"],
    ["changed", "sys_rc_changed"],
    ["late->on_time", "sys_rc_late_on_time"],
    ["late->early_close", "sys_rc_late_early"],
    ["events_to_void", "sys_rc_events"],
    ["over_6h", "sys_rc_over_6h"],
];

// The bilingual `detail` as text, never an object (a 422 validation detail is a list).
const errText = (e, t) => apiErrorText(e, t("error_generic"));

/**
 * «إعادة احتساب الإغلاق المتأخر» — runs scripts/recompute_checkin_close from the
 * app (the owner has no shell on production). «معاينة» is a dry run that writes
 * nothing; «تطبيق» is only offered after a preview that found something to do,
 * and asks first. Old values stay on each record; alerts are flagged, never
 * deleted. Super admin only — the page shows this card to super admins and the
 * server refuses anyone else.
 */
export default function CheckinRecomputeCard() {
    const { t, lang } = useI18n();
    const [preview, setPreview] = useState(null);
    const [applied, setApplied] = useState(null);
    // the last APPLIED run, from the audit log: undefined = not known (loading / error), null = none yet
    const [last, setLast] = useState(undefined);
    const [busy, setBusy] = useState(null); // "preview" | "apply" | null
    const [confirmOpen, setConfirmOpen] = useState(false);

    // page state forgets a run on reload; the audit log does not
    const loadLast = useCallback(async () => {
        try {
            const r = await api.get(`${RECOMPUTE_URL}/last`);
            setLast(r.data?.last || null);
        } catch (_e) {
            setLast(undefined);
        }
    }, []);
    useEffect(() => { loadLast(); }, [loadLast]);

    const pc = preview?.counts || {};
    const canApply = !!preview && ((pc.changed || 0) + (pc.events_to_void || 0)) > 0;

    const run = async (apply) => {
        if (busy) return;
        setBusy(apply ? "apply" : "preview");
        try {
            const r = await api.post(RECOMPUTE_URL, { apply });
            if (apply) {
                setApplied(r.data);
                setPreview(null);
                setConfirmOpen(false);
                toast.success(t("sys_rc_applied")
                    .replace("{n}", String(r.data.modified ?? 0))
                    .replace("{m}", String(r.data.voided ?? 0)));
                loadLast();
            } else {
                setPreview(r.data);
                setApplied(null);
            }
        } catch (e) {
            toast.error(errText(e, t));
        } finally {
            setBusy(null);
        }
    };

    const report = preview || applied;

    return (
        <Card data-testid={C.recomputeCard} className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
            <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
                <div className="min-w-0">
                    <CardTitle className="text-base font-bold text-[color:var(--ink)] flex items-center gap-2">
                        <History className="h-4 w-4 text-[color:var(--brand-orange)]" />
                        {t("sys_rc_title")}
                    </CardTitle>
                    <p className="text-xs text-[color:var(--ink-muted)] mt-0.5">{t("sys_rc_desc")}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <Button data-testid={C.recomputePreview} variant="soft" size="sm" className="gap-2"
                            onClick={() => run(false)} disabled={!!busy}>
                        {busy === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                        {t("sys_rc_preview")}
                    </Button>
                    <Button data-testid={C.recomputeApply} size="sm" className="gap-2"
                            onClick={() => setConfirmOpen(true)} disabled={!canApply || !!busy}>
                        <CheckCheck className="h-3.5 w-3.5" />
                        {t("sys_rc_apply")}
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {last ? (
                    <div data-testid={C.recomputeLast}
                         className="rounded-xl surface-2 border border-[color:var(--border-soft)] px-3 py-2 text-xs text-[color:var(--ink-muted)]">
                        <span className="font-semibold text-[color:var(--ink)]">{t("sys_rc_last")}: </span>
                        <bdi title={last.at || undefined}>{fmtRiyadhDateTime(last.at, lang)}</bdi>
                        {" · "}<bdi>{last.actor_name || last.actor_id || "—"}</bdi>
                        {" · "}{t("sys_rc_last_counts")
                            .replace("{n}", String(last.modified ?? 0))
                            .replace("{m}", String(last.voided ?? 0))}
                        {last.from || last.to ? (
                            <> {" · "}<bdi dir="ltr">{last.from || "…"} → {last.to || "…"}</bdi></>
                        ) : null}
                    </div>
                ) : last === null ? (
                    <p data-testid={C.recomputeLast} className="text-xs text-[color:var(--ink-faint)]">{t("sys_rc_last_none")}</p>
                ) : null}
                {preview ? (
                    <>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                            {COUNTS.map(([k, label]) => (
                                <div key={k} data-testid={C.recomputeCount(k)}
                                     className="rounded-xl surface-2 border border-[color:var(--border-soft)] px-3 py-2">
                                    <p className="text-[11px] text-[color:var(--ink-muted)]">{t(label)}</p>
                                    <p className="tnum text-xl font-extrabold text-[color:var(--ink)]">{pc[k] ?? 0}</p>
                                </div>
                            ))}
                        </div>
                        {!canApply ? (
                            <p className="text-xs text-[color:var(--ink-muted)]">{t("sys_rc_nothing")}</p>
                        ) : null}
                    </>
                ) : null}
                {applied ? (
                    <p data-testid={C.recomputeResult} className="text-sm font-semibold text-[color:var(--success)]">
                        {t("sys_rc_applied")
                            .replace("{n}", String(applied.modified ?? 0))
                            .replace("{m}", String(applied.voided ?? 0))}
                    </p>
                ) : null}
                {report && Array.isArray(report.lines) && report.lines.length ? (
                    <div>
                        <p className="text-[11px] text-[color:var(--ink-muted)] mb-1">{t("sys_rc_report")}</p>
                        <pre data-testid={C.recomputeLines} dir="ltr"
                             className="max-h-56 overflow-auto rounded-lg surface-2 border border-[color:var(--border-soft)] p-3 font-mono text-[10px] leading-relaxed text-[color:var(--ink-muted)] whitespace-pre-wrap">
                            {report.lines.map((x) => String(x)).join("\n")}
                        </pre>
                    </div>
                ) : null}
            </CardContent>

            <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!busy) setConfirmOpen(o); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("sys_rc_confirm_title")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("sys_rc_confirm_desc")
                                .replace("{n}", String(pc.changed ?? 0))
                                .replace("{m}", String(pc.events_to_void ?? 0))}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={!!busy}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={C.recomputeConfirm} disabled={!!busy}
                                           onClick={(e) => { e.preventDefault(); run(true); }}>
                            {busy === "apply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                            {t("sys_rc_apply")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}
